<!-- Loading State -->
@if (isLoading) {
  <div class="loading-container">
    <mat-spinner diameter="50"></mat-spinner>
    <p class="loading-text">Loading ownership data...</p>
  </div>
}

<!-- Error State -->
@if (error && !isLoading) {
  <div class="error-container">
    <mat-card class="error-card">
      <mat-card-content>
        <div class="error-content">
          <mat-icon class="error-icon">error</mat-icon>
          <h3>Error Loading Data</h3>
          <p>{{ error }}</p>
          <button mat-raised-button color="primary" (click)="retryLoad()">
            <mat-icon>refresh</mat-icon>
            Retry
          </button>
        </div>
      </mat-card-content>
    </mat-card>
  </div>
}

<!-- Main Content -->
@if (!isLoading && !error) {
  <div class="ownership-stats-container">
    <!-- Page Header -->
    <div class="page-header">
      <h1 class="page-title">Repository Ownership Statistics</h1>
      <p class="page-subtitle">Comprehensive overview of repository ownership, pipelines, and team analytics</p>
    </div>
    <!-- Tab Navigation -->
    <mat-tab-group [(selectedIndex)]="selectedTabIndex" (selectedTabChange)="onTabChange($event)"
      class="stats-tabs">
      <!-- Overview Tab -->
      <mat-tab label="Overview">
        <div class="tab-content">
          <!-- Summary Cards -->
          <div class="summary-cards">
            <mat-card class="summary-card repos-card">
              <mat-card-content>
                <div class="card-header">
                  <mat-icon class="card-icon">folder</mat-icon>
                  <span class="card-label">Total Repositories</span>
                </div>
                <div class="card-value">{{ overviewStats.totalRepos }}</div>
              </mat-card-content>
            </mat-card>
            <mat-card class="summary-card pipelines-card">
              <mat-card-content>
                <div class="card-header">
                  <mat-icon class="card-icon">build_circle</mat-icon>
                  <span class="card-label">Release Pipelines</span>
                </div>
                <div class="card-value">{{ overviewStats.totalReleasePipelines }}</div>
              </mat-card-content>
            </mat-card>
            <mat-card class="summary-card builds-card">
              <mat-card-content>
                <div class="card-header">
                  <mat-icon class="card-icon">construction</mat-icon>
                  <span class="card-label">Build Definitions</span>
                </div>
                <div class="card-value">{{ overviewStats.totalBuildDefinitions }}</div>
              </mat-card-content>
            </mat-card>
            <mat-card class="summary-card yaml-card">
              <mat-card-content>
                <div class="card-header">
                  <mat-icon class="card-icon">code</mat-icon>
                  <span class="card-label">YAML Pipelines</span>
                </div>
                <div class="card-value">{{ overviewStats.totalYamlPipelines }}</div>
              </mat-card-content>
            </mat-card>
            <mat-card class="summary-card stale-card">
              <mat-card-content>
                <div class="card-header">
                  <mat-icon class="card-icon">warning</mat-icon>
                  <span class="card-label">Stale Pipelines</span>
                </div>
                <div class="card-value">{{ overviewStats.stalePipelines }}</div>
              </mat-card-content>
            </mat-card>
          </div>
          <!-- Stale Pipelines Table -->
          <mat-card class="stale-pipelines-card">
            <mat-card-header>
              <mat-card-title>Most Stale Pipelines</mat-card-title>
              <mat-card-subtitle>Pipelines that haven't been run recently</mat-card-subtitle>
            </mat-card-header>
            <mat-card-content>
              <ag-grid-angular
                class="ag-theme-material"
                [rowData]="stalePipelineRows"
                [columnDefs]="pipelineColumnDefs"
              [defaultColDef]="{
                sortable: true,
                filter: true,
                resizable: true
              }"
                [pagination]="true"
                [paginationPageSize]="10"
                [paginationPageSizeSelector]="[10, 20, 50]"
                domLayout="autoHeight">
              </ag-grid-angular>
            </mat-card-content>
          </mat-card>
        </div>
      </mat-tab>
      <!-- Repositories Tab -->
      <mat-tab label="Repositories">
        <div class="tab-content">
          <mat-card class="data-table-card">
            <mat-card-header>
              <mat-card-title>Repository Overview</mat-card-title>
              <mat-card-subtitle>All repositories with their associated pipelines</mat-card-subtitle>
            </mat-card-header>
            <mat-card-content>
              <ag-grid-angular
                class="ag-theme-material repositories-grid"
                [rowData]="repositoryRows"
                [columnDefs]="repositoryColumnDefs"
              [defaultColDef]="{
                sortable: true,
                filter: true,
                resizable: true
              }"
                [pagination]="true"
                [paginationPageSize]="25"
                [paginationPageSizeSelector]="[10, 25, 50, 100]"
                [sideBar]="true"
                domLayout="normal">
              </ag-grid-angular>
            </mat-card-content>
          </mat-card>
        </div>
      </mat-tab>
      <!-- Pipelines Tab -->
      <mat-tab label="Pipelines">
        <div class="tab-content">
          <!-- Pipelines Table -->
          <mat-card class="data-table-card">
            <mat-card-header>
              <mat-card-title>Pipeline Analysis</mat-card-title>
              <mat-card-subtitle>Detailed pipeline information with status tracking</mat-card-subtitle>
            </mat-card-header>
            <mat-card-content>
              <ag-grid-angular
                class="ag-theme-material pipelines-grid"
                [rowData]="pipelineRows"
                [columnDefs]="pipelineColumnDefs"
              [defaultColDef]="{
                sortable: true,
                filter: true,
                resizable: true
              }"
                [pagination]="true"
                [paginationPageSize]="50"
                [paginationPageSizeSelector]="[25, 50, 100, 200]"
                [sideBar]="true"
                domLayout="normal">
              </ag-grid-angular>
            </mat-card-content>
          </mat-card>
        </div>
      </mat-tab>
      <!-- Team Stats Tab -->
      <mat-tab label="Team Stats">
        <div class="tab-content">
          <mat-card class="team-chart-card">
            <mat-card-header>
              <mat-card-title>Team Statistics</mat-card-title>
              <mat-card-subtitle>Repository and pipeline distribution across teams</mat-card-subtitle>
            </mat-card-header>
            <mat-card-content>
              <div class="chart-container">
                <canvas
                  baseChart
                  [type]="teamChartType"
                  [data]="teamChartData"
                  [options]="teamChartOptions"
                  class="team-stats-chart">
                </canvas>
              </div>
            </mat-card-content>
          </mat-card>
          <!-- Team Stats Table -->
          <mat-card class="data-table-card">
            <mat-card-header>
              <mat-card-title>Team Statistics Table</mat-card-title>
              <mat-card-subtitle>Detailed team statistics with repository and pipeline counts</mat-card-subtitle>
            </mat-card-header>
            <mat-card-content>
              <ag-grid-angular
                class="ag-theme-material team-stats-grid"
                [rowData]="teamStatsRows"
                [columnDefs]="teamStatsColumnDefs"
              [defaultColDef]="{
                sortable: true,
                filter: true,
                resizable: true
              }"
                [pagination]="true"
                [paginationPageSize]="25"
                [paginationPageSizeSelector]="[10, 25, 50]"
                [sideBar]="true"
                domLayout="normal">
              </ag-grid-angular>
            </mat-card-content>
          </mat-card>
        </div>
      </mat-tab>
      <!-- System Stats Tab -->
      <mat-tab label="System Stats">
        <div class="tab-content">
          <!-- System Stats Table -->
          <mat-card class="data-table-card">
            <mat-card-header>
              <mat-card-title>System Statistics</mat-card-title>
              <mat-card-subtitle>Repository and pipeline distribution across business systems</mat-card-subtitle>
            </mat-card-header>
            <mat-card-content>
              <!-- Systems Filter -->
              <div class="filters-panel" style="margin-bottom: 16px;">
                <mat-form-field appearance="outline" style="width: 300px;">
                  <mat-label>Filter by Systems</mat-label>
                  <mat-select [value]="selectedSystems" (selectionChange)="onSystemsFilterChange($event.value)" multiple>
                    @for (system of availableSystems; track system) {
                      <mat-option [value]="system">
                        {{ system }}
                      </mat-option>
                    }
                  </mat-select>
                </mat-form-field>
                <button mat-raised-button color="accent" (click)="clearFilters()" style="margin-left: 16px;">
                  <mat-icon>clear</mat-icon>
                  Clear Filters
                </button>
              </div>
              <ag-grid-angular
                class="ag-theme-material system-stats-grid"
                [rowData]="systemStatsRows"
                [columnDefs]="systemStatsColumnDefs"
              [defaultColDef]="{
                sortable: true,
                filter: true,
                resizable: true
              }"
                [pagination]="true"
                [paginationPageSize]="25"
                [paginationPageSizeSelector]="[10, 25, 50]"
                [sideBar]="true"
                domLayout="normal">
              </ag-grid-angular>
            </mat-card-content>
          </mat-card>
        </div>
      </mat-tab>
    </mat-tab-group>
  </div>
}
